'use client';

import type React from 'react';

import {
	useRef,
	useState,
	useCallback,
	useImperativeHandle,
	forwardRef,
	type Ref,
	useEffect,
	type ComponentType,
} from 'react';
import DataTable, {
	type SortOrder,
	type TableColumn,
	type ExpanderComponentProps,
} from 'react-data-table-component';
import {
	Accordion,
	AccordionBody,
	AccordionHeader,
	AccordionItem,
	Badge,
	Button,
	Card,
	CardBody,
	CardText,
	CardTitle,
	Col,
	Container,
	Input,
	Row,
	Spinner,
	Table,
	Tooltip,
} from 'reactstrap';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import { apiAdapter } from '../../../config/adapters/api.adapter';
import { API_URLS } from '../../../constants/api_links';

interface ActionsConfig {
	importUrl: string;
	exportUrl: string;
	templateUrl: string;
}

interface ExpandableConfig<T> {
	component: ComponentType<ExpanderComponentProps<T>>;
	expandableRowDisabled?: (row: T) => boolean;
	expandOnRowClicked?: boolean;
	expandOnRowDoubleClicked?: boolean;
	expandableRowsHideExpander?: boolean;
}

interface Props<T, R> {
	route: string;
	columns: TableColumn<T>[];
	mapper?: (response: R) => { data: T[]; total: number };
	showSearch?: boolean;
	showButtons?: boolean;
	actionsConfig?: ActionsConfig;
	debounceTime?: number;
	expandableConfig?: ExpandableConfig<T>;
}

export interface CustomTableRef {
	refetch: () => void;
}

interface TooltipState {
	import: boolean;
	export: boolean;
	template: boolean;
}

const INITIAL_PAGE = 1;
const INITIAL_PER_PAGE = 10;
const INITIAL_SORT_FIELD = 'id';
const INITIAL_SORT_DIRECTION: SortOrder = 'desc' as SortOrder;

const useDebounce = (value: string, delay: number) => {
	const [debouncedValue, setDebouncedValue] = useState(value);

	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedValue(value);
		}, delay);

		return () => {
			clearTimeout(handler);
		};
	}, [value, delay]);

	return debouncedValue;
};

const CustomTableInner = <T, R>(
	{
		route,
		columns,
		mapper,
		showSearch = false,
		showButtons = false,
		actionsConfig,
		debounceTime = 400,
		expandableConfig,
	}: Props<T, R>,
	ref: Ref<CustomTableRef>
) => {
	const [search, setSearch] = useState('');
	const [page, setPage] = useState(INITIAL_PAGE);
	const [perPage, setPerPage] = useState(INITIAL_PER_PAGE);
	const [sortField, setSortField] = useState(INITIAL_SORT_FIELD);
	const [sortDirection, setSortDirection] = useState(INITIAL_SORT_DIRECTION);
	const [tooltipOpen, setTooltipOpen] = useState<TooltipState>({
		import: false,
		export: false,
		template: false,
	});
	const [isImporting, setIsImporting] = useState(false);
	const [isExporting, setIsExporting] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const debouncedSearch = useDebounce(search, debounceTime);

	const toggleTooltip = useCallback((key: keyof TooltipState) => {
		setTooltipOpen((prev) => ({ ...prev, [key]: !prev[key] }));
	}, []);

	const fetchData = async () => {
		const url = `${route}?page=${page}&per_page=${perPage}&sort=${sortField}&sort_direction=${sortDirection}&search=${search}`;
		const response = await apiAdapter.get<R>(url);
		if (mapper) return mapper(response);
		return {
			data: (response as any)?.data?.data || [],
			total: (response as any)?.data?.total || 0,
		};
	};

	const { data, isLoading, refetch } = useQuery({
		queryKey: [
			'data',
			route,
			page,
			perPage,
			sortField,
			sortDirection,
			debouncedSearch,
		],
		queryFn: fetchData,
	});

	useImperativeHandle(ref, () => ({
		refetch: () => {
			console.log('Refetch externo ejecutado');
			refetch({ cancelRefetch: false });
		},
	}));

	const handleImportClick = () => fileInputRef.current?.click();

	const handleFileChange = async (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		const file = event.target.files?.[0];
		if (!file || !actionsConfig?.importUrl) return;

		const formData = new FormData();
		formData.append('file', file);

		try {
			setIsImporting(true);
			await apiAdapter.post(actionsConfig.importUrl, formData, {
				headers: { 'Content-Type': 'multipart/form-data' },
			});
			toast.success('Archivo importado exitosamente');
			await refetch();
		} catch (error) {
			console.error('Error al importar:', error);
			toast.error('Error al importar archivo');
		} finally {
			setIsImporting(false);
			if (fileInputRef.current) fileInputRef.current.value = '';
		}
	};

	const handleExport = async () => {
		try {
			setIsExporting(true);
			const response = await apiAdapter.get(API_URLS.EXPORT_INTEREST, {
				responseType: 'blob',
			});
			const blob = new Blob([response as Blob]);
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', 'interests.xlsx');
			document.body.appendChild(link);
			link.click();
			window.URL.revokeObjectURL(url);
		} catch (error) {
			console.error('Error al exportar el archivo:', error);
			toast.error('Error al exportar el archivo');
		} finally {
			setIsExporting(false);
		}
	};

	const handleDownloadTemplate = async () => {
		if (!actionsConfig?.templateUrl) {
			toast.error('No se encontró la URL de la plantilla');
			return;
		}
		try {
			const response = await apiAdapter.get(actionsConfig.templateUrl, {
				responseType: 'blob',
			});
			const blob = new Blob([response as Blob]);
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', 'interest_template.xlsx');
			document.body.appendChild(link);
			link.click();
			window.URL.revokeObjectURL(url);
			toast.success('Plantilla descargada exitosamente');
		} catch (error) {
			console.error('Error al descargar la plantilla:', error);
			toast.error('Error al descargar la plantilla');
		}
	};

	const handleSort = (column: any, direction: SortOrder) => {
		console.log(column);
		setSortField(column.sortField || INITIAL_SORT_FIELD);
		setSortDirection(direction);
		setPage(INITIAL_PAGE);
	};

	const handleChangeRowsPerPage = (newPerPage: number, newPage: number) => {
		setPerPage(newPerPage);
		setPage(newPage);
	};

	const handleChangePage = (newPage: number) => {
		setPage(newPage);
	};

	return (
		<Container>
			{(showSearch || showButtons) && (
				<Row className="mb-3 align-items-center">
					{showSearch && (
						<Col md={6}>
							<Input
								type="text"
								placeholder="Buscar..."
								className="form-control"
								value={search}
								onChange={(e) => setSearch(e.target.value)}
							/>
						</Col>
					)}
					{showButtons && actionsConfig && (
						<Col md={6} className="text-end">
							<input
								type="file"
								ref={fileInputRef}
								style={{ display: 'none' }}
								onChange={handleFileChange}
								accept=".csv,.xlsx"
							/>
							<Button
								id="import-btn"
								color="primary"
								className="me-2"
								onClick={handleImportClick}
								disabled={isImporting}
							>
								{isImporting ? (
									<Spinner size="sm" />
								) : (
									<i className="fa fa-upload"></i>
								)}
							</Button>
							<Tooltip
								isOpen={tooltipOpen.import}
								target="import-btn"
								toggle={() => toggleTooltip('import')}
							>
								Importar
							</Tooltip>

							<Button
								id="export-btn"
								color="success"
								className="me-2"
								onClick={handleExport}
								disabled={isExporting}
							>
								{isExporting ? (
									<Spinner size="sm" />
								) : (
									<i className="fa fa-download"></i>
								)}
							</Button>
							<Tooltip
								isOpen={tooltipOpen.export}
								target="export-btn"
								toggle={() => toggleTooltip('export')}
							>
								Exportar
							</Tooltip>

							<Button
								id="template-btn"
								color="info"
								onClick={handleDownloadTemplate}
							>
								<i className="fa fa-file-excel"></i>
							</Button>
							<Tooltip
								isOpen={tooltipOpen.template}
								target="template-btn"
								toggle={() => toggleTooltip('template')}
							>
								Descargar Plantilla
							</Tooltip>
						</Col>
					)}
				</Row>
			)}
			<Row>
				<Col>
					<DataTable
						data={data?.data || []}
						columns={columns}
						pagination
						paginationServer
						sortServer
						highlightOnHover
						striped
						paginationTotalRows={data?.total || 0}
						progressPending={isLoading}
						onSort={handleSort}
						onChangeRowsPerPage={handleChangeRowsPerPage}
						onChangePage={handleChangePage}
						className="table-responsive"
						customStyles={{
							table: {
								style: {
									minHeight: '400px',
								},
							},
						}}
						expandableRows={!!expandableConfig}
						expandableRowsComponent={expandableConfig?.component}
						expandableRowDisabled={expandableConfig?.expandableRowDisabled}
						expandOnRowClicked={expandableConfig?.expandOnRowClicked}
						expandOnRowDoubleClicked={
							expandableConfig?.expandOnRowDoubleClicked
						}
						expandableRowsHideExpander={
							expandableConfig?.expandableRowsHideExpander
						}
					/>
				</Col>
			</Row>
		</Container>
	);
};

// Export correcto con tipado genérico + ref
export const CustomTable = forwardRef(CustomTableInner) as <T, R>(
	props: Props<T, R> & { ref?: Ref<CustomTableRef> }
) => ReturnType<typeof CustomTableInner>;

const ExpandedComponent = ({ data }: { data: any }) => {
	return (
		<div style={{ padding: '15px', backgroundColor: '#f8f9fa' }}>
			<Accordion open="" toggle={() => {}}>
				{/* Información Principal de Financial Assistance */}
				<AccordionItem>
					<AccordionHeader targetId="1">
						<strong>Información de la Asistencia Financiera</strong>
					</AccordionHeader>
					<AccordionBody accordionId="1">
						<Row>
							<Col md={6}>
								<Card
									className="mb-3"
									style={{ borderLeft: '4px solid #007bff' }}
								>
									<CardBody>
										<CardTitle tag="h6" className="text-primary">
											Datos Principales
										</CardTitle>
										<CardText>
											<strong>Monto Solicitado:</strong> $
											{data.requested_amount?.toLocaleString()}
										</CardText>
										<CardText>
											<strong>Cuotas:</strong> {data.installments}
										</CardText>
										<CardText>
											<strong>Estado:</strong>
											<Badge
												color={
													data.status === 'approved'
														? 'success'
														: data.status === 'pending'
															? 'warning'
															: 'secondary'
												}
												className="ms-2"
											>
												{data.status}
											</Badge>
										</CardText>
									</CardBody>
								</Card>
							</Col>

							<Col md={6}>
								<Card
									className="mb-3"
									style={{ borderLeft: '4px solid #28a745' }}
								>
									<CardBody>
										<CardTitle tag="h6" className="text-success">
											Referencia Familiar
										</CardTitle>
										<CardText>
											<strong>Nombre:</strong>{' '}
											{data.name_family_reference || 'No especificado'}
										</CardText>
										<CardText>
											<strong>Teléfono:</strong>{' '}
											{data.phone_family_reference || 'No especificado'}
										</CardText>
									</CardBody>
								</Card>
							</Col>
						</Row>
					</AccordionBody>
				</AccordionItem>

				{/* Información del Usuario Externo */}
				{data.external_user && (
					<AccordionItem>
						<AccordionHeader targetId="2">
							<strong>Información del Usuario</strong>
						</AccordionHeader>
						<AccordionBody accordionId="2">
							<Row>
								<Col md={6}>
									<Card
										className="mb-3"
										style={{ borderLeft: '4px solid #ffc107' }}
									>
										<CardBody>
											<CardTitle tag="h6" className="text-warning">
												Datos Personales
											</CardTitle>
											{data.external_user.user && (
												<>
													<CardText>
														<strong>Nombre:</strong>{' '}
														{data.external_user.user.name}
													</CardText>
													<CardText>
														<strong>Email:</strong>{' '}
														{data.external_user.user.email}
													</CardText>
													<CardText>
														<strong>Teléfono:</strong>{' '}
														{data.external_user.user.phone || 'No especificado'}
													</CardText>
												</>
											)}
										</CardBody>
									</Card>
								</Col>

								<Col md={6}>
									<Card
										className="mb-3"
										style={{ borderLeft: '4px solid #17a2b8' }}
									>
										<CardBody>
											<CardTitle tag="h6" className="text-info">
												Información Bancaria
											</CardTitle>
											{data.external_user.bank && (
												<>
													<CardText>
														<strong>Banco:</strong>{' '}
														{data.external_user.bank.name}
													</CardText>
													<CardText>
														<strong>Cuenta:</strong>{' '}
														{data.external_user.bank.account_number ||
															'No especificado'}
													</CardText>
													<CardText>
														<strong>Tipo:</strong>{' '}
														{data.external_user.bank.account_type ||
															'No especificado'}
													</CardText>
												</>
											)}
										</CardBody>
									</Card>
								</Col>
							</Row>
						</AccordionBody>
					</AccordionItem>
				)}

				{/* Portafolio de Crédito */}
				{data.credit_portfolio && (
					<AccordionItem>
						<AccordionHeader targetId="3">
							<strong>Portafolio de Crédito</strong>
						</AccordionHeader>
						<AccordionBody accordionId="3">
							<Card className="mb-3">
								<CardBody>
									<Row>
										<Col md={4}>
											<CardText>
												<strong>Fecha de Operación:</strong>{' '}
												{new Date(
													data.credit_portfolio.operation_date
												).toLocaleDateString()}
											</CardText>
											<CardText>
												<strong>Deuda Inicial:</strong> $
												{data.credit_portfolio.initial_debt?.toLocaleString()}
											</CardText>
											<CardText>
												<strong>Obligación Financiera:</strong> $
												{data.credit_portfolio.financial_obligation?.toLocaleString()}
											</CardText>
										</Col>
										<Col md={4}>
											<CardText>
												<strong>Seguro:</strong> $
												{data.credit_portfolio.insurance?.toLocaleString()}
											</CardText>
											<CardText>
												<strong>Cuota:</strong> $
												{data.credit_portfolio.fee?.toLocaleString()}
											</CardText>
											<CardText>
												<strong>Tasa de Crédito:</strong>{' '}
												{data.credit_portfolio.credit_rate}%
											</CardText>
										</Col>
										<Col md={4}>
											<CardText>
												<strong>Periodos:</strong>{' '}
												{data.credit_portfolio.periods}
											</CardText>
											<CardText>
												<strong>Periodicidad:</strong>{' '}
												{data.credit_portfolio.periodicity}
											</CardText>
										</Col>
									</Row>
								</CardBody>
							</Card>

							{/* Amortizaciones */}
							{data.credit_portfolio.external_amortizations &&
								data.credit_portfolio.external_amortizations.length > 0 && (
									<Card>
										<CardBody>
											<CardTitle tag="h6" className="mb-3">
												Plan de Amortizaciones
											</CardTitle>
											<Table striped responsive>
												<thead>
													<tr>
														<th>Periodo</th>
														<th>Fecha Pago</th>
														<th>Saldo Inicial</th>
														<th>Pago</th>
														<th>Amortización</th>
														<th>Interés</th>
														<th>Seguro</th>
														<th>Cuota</th>
														<th>Saldo Final</th>
														<th>Estado Pago</th>
													</tr>
												</thead>
												<tbody>
													{data.credit_portfolio.external_amortizations.map(
														(amortization: any, index: number) => (
															<tr key={index}>
																<td>{amortization.payment_periods}</td>
																<td>
																	{new Date(
																		amortization.payment_date
																	).toLocaleDateString()}
																</td>
																<td>
																	$
																	{amortization.initial_balance?.toLocaleString()}
																</td>
																<td>
																	${amortization.payment?.toLocaleString()}
																</td>
																<td>
																	${amortization.amortization?.toLocaleString()}
																</td>
																<td>
																	${amortization.interest?.toLocaleString()}
																</td>
																<td>
																	${amortization.insurance?.toLocaleString()}
																</td>
																<td>${amortization.fee?.toLocaleString()}</td>
																<td>
																	$
																	{amortization.final_balance?.toLocaleString()}
																</td>
																<td>
																	<Badge
																		color={
																			amortization.payment
																				? 'success'
																				: 'warning'
																		}
																	>
																		{amortization.payment
																			? 'Pagado'
																			: 'Pendiente'}
																	</Badge>
																</td>
															</tr>
														)
													)}
												</tbody>
											</Table>
										</CardBody>
									</Card>
								)}
						</AccordionBody>
					</AccordionItem>
				)}
			</Accordion>
		</div>
	);
};

export default ExpandedComponent;
